/**
 * The public page, section by section, mirroring the printed NPSO.
 *
 * This is the WHOLE form. It started as a curtailed subset — no supplier, no
 * item or mould cost, no purchasing notes — on the reasoning that a forwarded
 * link should not carry margin. That was overruled: the recipients are the
 * sales and purchasing colleagues who have to sign the thing off, and a
 * forecast made without the cost, the MOQ or what is in the box is a guess.
 * They see the same page they would have been emailed as a PDF.
 *
 * The PIN, the expiry and the attempt cap are what protect this, not the field
 * list. Keep every field here read-only unless it is on the printed form as
 * something to fill in — `editable` is the security boundary, and widening it
 * is a different decision from widening what is shown.
 *
 * Order follows the printed sheet, top to bottom. Labels are what the PDF
 * prints, which is not always the AcroForm name.
 */
export const NPSO_REQUEST_SECTIONS = [
    {
        title: 'Product',
        fields: [
            { pdfField: 'Brand', label: 'Brand' },
            { pdfField: 'Fixture', label: 'Fixture' },
            { pdfField: 'Item Number', label: 'Item Number' },
            { pdfField: 'Item Type', label: 'Item Type' },
            { pdfField: 'Category / Acct Set', label: 'Category / Acct Set' },
            { pdfField: 'Supplier', label: 'Supplier' },
            { pdfField: 'MFG Item#', label: 'MFG Item #' },
            { pdfField: 'Item Code', label: 'Item Code' },
            { pdfField: 'UPC Code', label: 'UPC Code' },
            { pdfField: 'US HS Code', label: 'US HS Code' },
            { pdfField: 'US HS Code Description', label: 'US HS Code Description' },
            { pdfField: 'Replacement Item', label: 'Replacement Item' },
            { pdfField: 'Launch Type', label: 'Launch Type' },
            { pdfField: 'EU Power', label: 'EU Power Cable' },
            { pdfField: 'PG Video', label: 'PG Video' },
            { pdfField: "Today's Date", label: 'Date' },
        ],
    },
    {
        title: 'Cost',
        fields: [
            { pdfField: 'Item Cost', label: 'Item Cost' },
            { pdfField: 'EU Cost', label: 'EU Cost' },
            { pdfField: 'Alt Cost', label: 'Alt Cost' },
            { pdfField: 'Alt Reason', label: 'Alt Cost Reason' },
            { pdfField: 'Mold Cost', label: 'Mold Cost' },
            { pdfField: 'Mold Share', label: 'Mold Share' },
        ],
    },
    {
        title: 'Pricing Data',
        fields: [
            { pdfField: 'US List', label: 'US LIST Price' },
            { pdfField: 'Discount Structure', label: 'Discount' },
            { pdfField: 'Multiplier', label: 'Multiplier (Cost to List)' },
            { pdfField: 'MAP Price', label: 'US MAP Price' },
            { pdfField: 'EU List', label: 'Euro List Price', editable: true },
            { pdfField: 'UK List', label: 'UK List Price', editable: true },
            { pdfField: 'EXP List', label: 'Export List Price', editable: true },
            { pdfField: 'Mex List', label: 'MEX List Price', editable: true },
        ],
    },
    {
        title: 'Ordering & Packaging',
        fields: [
            { pdfField: 'MOQ-US', label: 'MOQ - US' },
            { pdfField: 'MOQ-EU', label: 'MOQ - EU' },
            { pdfField: 'MasterPack', label: 'Master Pack' },
            { pdfField: 'Certifications', label: 'Certifications' },
            { pdfField: 'WhatsIncluded', label: "What's Included" },
        ],
    },
    {
        title: 'Sales Forecasting — Average Monthly Sales: Months 1-3',
        hiddenWhen: 'isNonForecastItem',
        fields: [
            { pdfField: 'US Dom 1-3', label: 'US Domestic', editable: true },
            { pdfField: 'US Exp 1-3', label: 'US Export', editable: true },
            { pdfField: 'EU Dom 1-3', label: 'EU Domestic', editable: true },
            { pdfField: 'EU Exp 1-3', label: 'EU Export', editable: true },
        ],
    },
    {
        // The AcroForm names in this block carry irregular spacing ("US Exp4",
        // "EU Exp 4"). They are matched exactly against the PDF — tidying them
        // silently blanks the field on export.
        title: 'Sales Forecasting — Average Monthly Sales: Month 4+',
        hiddenWhen: 'isNonForecastItem',
        fields: [
            { pdfField: 'US Dom 4', label: 'US Domestic', editable: true },
            { pdfField: 'US Exp4', label: 'US Export', editable: true },
            { pdfField: 'EU Dom 4', label: 'EU Domestic', editable: true },
            { pdfField: 'EU Exp 4', label: 'EU Export', editable: true },
        ],
    },
    {
        // The two totals stay read-only: they are the sum of the six above and a
        // recipient typing a third number over them is a disagreement, not data.
        title: 'Non-Forecast Quantities',
        gatedBy: 'isNonForecastItem',
        fields: [
            { pdfField: 'US Dom NF', label: 'US Domestic', editable: true },
            { pdfField: 'US Exp NF', label: 'US Export', editable: true },
            { pdfField: 'EU Dom NF', label: 'EU Domestic', editable: true },
            { pdfField: 'EU ExpNF', label: 'EU Export', editable: true },
            { pdfField: 'MinOnHand-US', label: 'Min On Hand - US', editable: true },
            { pdfField: 'MinOnHand-EU', label: 'Min On Hand - EU', editable: true },
            { pdfField: 'US Total', label: 'US Total' },
            { pdfField: 'EU Total', label: 'EU Total' },
        ],
    },
    {
        title: 'Notes & Sign-off',
        fields: [
            { pdfField: 'NotePur', label: 'Notes for Purchasing' },
            { pdfField: 'NotesSup', label: 'Notes for Supplier' },
            { pdfField: 'PCF', label: 'PCF on file' },
            { pdfField: 'CKR', label: 'CKR' },
            { pdfField: 'PG', label: 'PG' },
        ],
    },
];
/**
 * Every key a public submission could ever write — the union across all
 * sections, gated ones included. Derived from the sections above so the page
 * and the server allow-list can never drift apart.
 *
 * This is the SUPERSET. What a given product actually unlocks is
 * `npsoRequestEditableFields(metadata)`, because a gated section is neither
 * shown nor writable unless its flag is set. Use the function anywhere a real
 * product is in hand; this constant is for the cases where none is.
 */
export const NPSO_REQUEST_EDITABLE_FIELDS = NPSO_REQUEST_SECTIONS.flatMap((s) => s.fields.filter((f) => f.editable).map((f) => f.pdfField));
export const NPSO_REQUEST_PARTS = [
    {
        key: 'us',
        label: 'US',
        fields: ['US Dom 1-3', 'US Exp 1-3', 'US Dom 4', 'US Exp4', 'US Dom NF', 'US Exp NF', 'MinOnHand-US'],
    },
    {
        key: 'eu',
        label: 'Europe',
        fields: ['EU List', 'EU Dom 1-3', 'EU Exp 1-3', 'EU Dom 4', 'EU Exp 4', 'EU Dom NF', 'EU ExpNF', 'MinOnHand-EU'],
    },
    { key: 'uk', label: 'UK', fields: ['UK List'] },
    { key: 'export', label: 'Export & Mexico', fields: ['EXP List', 'Mex List'] },
];
/**
 * The "Assign a whole region" buttons in the request dialog (#1058).
 *
 * Deliberately NOT `NPSO_REQUEST_PARTS`. Those keys are stored on requests
 * sent between #931 and per-field assignment and still decide what those
 * links unlock, so they cannot change. A button only fills the per-field
 * assignment, which is what gets sent, so it is free to split by who owns the
 * numbers: domestic US, domestic EU (UK included), and everything export.
 * Every editable field is on exactly one button; the test enforces it.
 */
export const NPSO_ASSIGN_SHORTCUTS = [
    {
        key: 'us-domestic',
        label: 'US Domestic',
        fields: ['US Dom 1-3', 'US Dom 4', 'US Dom NF', 'MinOnHand-US'],
    },
    {
        key: 'eu-domestic',
        label: 'EU Domestic',
        fields: ['EU List', 'UK List', 'EU Dom 1-3', 'EU Dom 4', 'EU Dom NF', 'MinOnHand-EU'],
    },
    {
        key: 'export',
        label: 'Export & Mexico',
        fields: ['EXP List', 'Mex List', 'US Exp 1-3', 'EU Exp 1-3', 'US Exp4', 'EU Exp 4', 'US Exp NF', 'EU ExpNF'],
    },
];
export function npsoRequestPart(key) {
    return key === undefined ? undefined : NPSO_REQUEST_PARTS.find((p) => p.key === key);
}
/**
 * The fields THIS request asks for, narrowed by the product's section gates.
 *
 * Three generations of request, newest first:
 *
 *   1. An explicit field list — what the sender assigned to this person.
 *   2. A region key (#931) — the whole of one territory.
 *   3. Neither (#691) — the whole editable set, which is what it asked for
 *      when it went out. Do not "tidy" that into a default: it would silently
 *      shrink what an outstanding link is still collecting.
 *
 * Whatever the generation, the result is INTERSECTED with the editable set. The
 * stored list can only ever shrink what a token unlocks, never widen it, so a
 * tampered request document is worth nothing: a field name that is not editable
 * on this product contributes nothing, and neither does a region that has since
 * been deleted from the constant above. That is rule 1 of the service that owns
 * this, and it is the whole reason a bearer token in somebody's inbox is safe.
 *
 * An empty `fields` asks for NOTHING. That is a real state, not a bug: a field
 * nobody was assigned is not needed, so it is never chased and never holds up
 * completion.
 */
export function npsoRequestedFields(scope, metadata = {}) {
    const editable = npsoRequestEditableFields(metadata);
    if (scope?.fields)
        return editable.filter((key) => scope.fields.includes(key));
    if (scope?.part !== undefined) {
        const fields = npsoRequestPart(scope.part)?.fields ?? [];
        return editable.filter((key) => fields.includes(key));
    }
    return editable;
}
/**
 * Turn a field-by-field assignment into the requests to send.
 *
 * Several people on one field is the point: any of them may answer it, and a
 * box one of them fills is filled for all of them. So people are grouped by the
 * SET of fields they were given, not one link each — two colleagues assigned
 * exactly the same numbers share a link and a PIN, which is one fewer PIN for
 * the sender to hand out and one fewer inbox to chase. Anyone with a different
 * set gets their own.
 *
 * Addresses are lower-cased and de-duplicated; a field assigned to nobody is
 * simply absent from the result, which is what makes it "not needed".
 *
 * Returned in the order the fields are declared, so the sender reads the groups
 * down the form rather than in hash order.
 */
export function npsoRequestGroups(assignment) {
    const byPerson = new Map();
    for (const field of NPSO_REQUEST_EDITABLE_FIELDS) {
        for (const raw of assignment[field] ?? []) {
            const email = raw.trim().toLowerCase();
            if (!email)
                continue;
            const owned = byPerson.get(email) ?? [];
            if (!owned.includes(field))
                owned.push(field);
            byPerson.set(email, owned);
        }
    }
    const groups = new Map();
    for (const [email, fields] of byPerson) {
        // The signature is the sorted set, so two people given the same fields in a
        // different order still share one link.
        const key = [...fields].sort().join('\u0000');
        const group = groups.get(key) ?? { recipients: [], fields };
        group.recipients.push(email);
        groups.set(key, group);
    }
    return [...groups.values()];
}
/**
 * Does this section apply to this product? The one place the gates are read,
 * so the dialog, the public page, the allow-list and the email agree.
 */
export function npsoSectionInPlay(section, metadata = {}) {
    if (section.gatedBy && !isMetadataFlagSet(metadata[section.gatedBy]))
        return false;
    if (section.hiddenWhen && isMetadataFlagSet(metadata[section.hiddenWhen]))
        return false;
    return true;
}
/**
 * Every NPSO field in a block that does NOT apply to this product (#1062).
 *
 * An NPSO is a forecasted OR a non-forecasted item, so one of the two blocks is
 * always out. Read-only fields (US/EU Total) are included: they belong to the
 * non-forecast block and mean nothing on a forecast item. The export blanks
 * these, the approval page and the form editor hide them.
 */
export function npsoFieldsOutOfPlay(metadata = {}) {
    return new Set(NPSO_REQUEST_SECTIONS.filter((s) => !npsoSectionInPlay(s, metadata)).flatMap((s) => s.fields.map((f) => f.pdfField)));
}
/**
 * Is a product metadata flag set?
 *
 * One vocabulary for what counts as a tick, shared by the section gates and by
 * how a checkbox is printed: a boolean, "Yes", "true" and "1" are all set;
 * blank, absent and an explicit negative are not.
 */
export function isMetadataFlagSet(raw) {
    if (raw === undefined || raw === null)
        return false;
    if (typeof raw === 'boolean')
        return raw;
    const value = String(raw).trim().toLowerCase();
    if (value === '')
        return false;
    return !['false', 'no', 'n', '0', 'off', 'unchecked'].includes(value);
}
/**
 * The keys THIS product's request unlocks. A gated section contributes nothing
 * unless the product carries its flag, so a forecast item is never asked for
 * non-forecast quantities and can still reach "complete".
 */
export function npsoRequestEditableFields(metadata = {}) {
    return NPSO_REQUEST_SECTIONS.filter((s) => npsoSectionInPlay(s, metadata)).flatMap((s) => s.fields.filter((f) => f.editable).map((f) => f.pdfField));
}
/**
 * How many of the requested fields currently hold a value.
 *
 * "Filled" means non-empty, NOT "the recipient typed it". Some fields may
 * already have had values when the request went out, and what the PM is waiting
 * on is whether the form can be signed off — not who supplied which number.
 * A zero or a "0.00" counts as filled; only blank does not.
 */
export function countFilledRequestedFields(values, fields = NPSO_REQUEST_EDITABLE_FIELDS) {
    return fields.filter((key) => {
        const value = values[key];
        if (value === undefined || value === null)
            return false;
        return typeof value === 'string' ? value.trim() !== '' : true;
    }).length;
}
/** Templates that can be sent out for external fill-out. NPSO only, for now. */
export const REQUESTABLE_TEMPLATE_IDS = ['npso'];
/** Six digits. Space is 1e6, so the attempt cap below is what actually guards it. */
export const FORM_REQUEST_PIN_LENGTH = 6;
/**
 * Failed PIN attempts before a link is dead for good.
 *
 * This, not the rate limiter, is the real defence: a limiter keyed on IP is
 * beaten by a botnet, and 10 guesses against a 1-in-a-million space is a
 * 0.001% chance. A locked-out colleague asks for a new link, which is one
 * click for the PM.
 */
export const FORM_REQUEST_PIN_MAX_ATTEMPTS = 10;
//# sourceMappingURL=product-form-request.types.js.map